// SettingsPage — manage your editor profile + (admin-only) editors list.
//
// Profile actions: update name/bio, upload avatar, change password.
// Editors list is read-only here; PB schema controls whether the
// "Invite editor" affordance succeeds based on the actor's role.

import { useEffect, useRef, useState } from "react";
import { list, updateRecord, fileUrl, createRecord } from "../lib/pb";
import { useT, useLangStore, type Language } from "../lib/i18n";
import { resetOnboarding } from "../components/OnboardingFlow";
import type { Editor } from "../lib/types";

interface Props {
  editor: Editor;
  onUpdated: (e: Editor) => void;
}

export default function SettingsPage({ editor, onUpdated }: Props) {
  const t = useT();
  const lang = useLangStore((s) => s.lang);
  const setLang = useLangStore((s) => s.setLang);
  const [name, setName] = useState(editor.name);
  const [bio, setBio] = useState(editor.bio || "");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNew2, setPwNew2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [editors, setEditors] = useState<Editor[]>([]);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    list<Editor>("editors", { perPage: 100, sort: "name" })
      .then((r) => setEditors(r.items))
      .catch(() => setEditors([editor]));
  }, [editor]);

  async function saveProfile() {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim() || editor.name);
      fd.append("bio", bio);
      if (avatarFile) fd.append("avatar", avatarFile);
      const updated = await updateRecord<Editor>("editors", editor.id, fd);
      setAvatarFile(null);
      onUpdated(updated);
      alert(t.settingsProfileSaved);
    } catch (e) {
      alert(`${t.settingsProfile}: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function changePassword() {
    if (pwNew.length < 8) { alert(t.settingsPasswordError); return; }
    if (pwNew !== pwNew2) { alert(t.settingsPasswordError); return; }
    setPwBusy(true);
    try {
      await updateRecord("editors", editor.id, {
        oldPassword: pwOld,
        password: pwNew,
        passwordConfirm: pwNew2,
      });
      setPwOld(""); setPwNew(""); setPwNew2("");
      alert(t.settingsPasswordChanged);
    } catch (e) {
      alert(`${t.settingsPasswordError}: ${(e as Error).message}`);
    } finally {
      setPwBusy(false);
    }
  }

  function replayOnboarding() {
    resetOnboarding();
    // Диспатчим event — App.tsx подписан и откроет overlay сразу,
    // без перезагрузки страницы.
    window.dispatchEvent(new CustomEvent("reverium-editorial-replay-onboarding"));
  }

  async function invite() {
    if (!inviteEmail.includes("@") || !inviteName.trim()) {
      alert("Provide a valid email and name.");
      return;
    }
    setInviting(true);
    try {
      // Generate a long random password — editor resets via "forgot password"
      // (server endpoint not yet exposed in admin; we surface the password
      //  one-time so the admin can hand it off).
      const tmpPassword = cryptoRandomPassword();
      await createRecord<Editor>("editors", {
        email: inviteEmail.trim(),
        name: inviteName.trim(),
        password: tmpPassword,
        passwordConfirm: tmpPassword,
        active: true,
      });
      const r = await list<Editor>("editors", { perPage: 100, sort: "name" });
      setEditors(r.items);
      setInviteEmail(""); setInviteName("");
      alert(`Editor created.\nEmail: ${inviteEmail}\nTemp password: ${tmpPassword}\n\nShare it securely.`);
    } catch (e) {
      alert(`Invite failed: ${(e as Error).message}`);
    } finally {
      setInviting(false);
    }
  }

  const avatarUrl = avatarFile
    ? URL.createObjectURL(avatarFile)
    : editor.avatar ? fileUrl("editors", editor.id, editor.avatar, "120x120") : null;

  return (
    <div className="page page-narrow">
      <div className="page-header">
        <h1 className="page-title">{t.settingsTitle}</h1>
      </div>

      {/* Profile */}
      <section className="card" style={{ padding: 22, marginBottom: 18 }}>
        <h2 style={sectionTitle}>{t.settingsProfile}</h2>
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          <div style={{ flexShrink: 0 }}>
            <div
              style={{
                width: 96, height: 96,
                borderRadius: 100,
                background: "var(--bg-3)",
                overflow: "hidden",
                border: "1px solid var(--border-1)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--text-3)",
              }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontFamily: "var(--font-serif)", fontSize: 32 }}>
                  {editor.name.slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: "none" }}
              onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
            />
            <button onClick={() => fileRef.current?.click()} className="btn btn-secondary btn-sm" style={{ marginTop: 10, width: "100%" }}>
              {avatarFile ? t.done : t.settingsUploadAvatar}
            </button>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label={t.loginEmail}>
              <input value={editor.email} disabled className="input" />
            </Field>
            <Field label={t.settingsName}>
              <input value={name} onChange={(e) => setName(e.target.value)} className="input" />
            </Field>
            <Field label={t.settingsBio}>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} className="textarea" rows={3} />
            </Field>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveProfile} disabled={saving} className="btn btn-primary">
                {saving ? t.loading : t.settingsSaveProfile}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Language */}
      <section className="card" style={{ padding: 22, marginBottom: 18 }}>
        <h2 style={sectionTitle}>{t.settingsLanguage}</h2>
        <p style={{ fontSize: 12, color: "var(--text-3)", margin: "0 0 14px", lineHeight: 1.5 }}>
          {t.settingsLanguageHint}
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <LangPill label="English" code="en" current={lang} onPick={setLang} />
          <LangPill label="Русский" code="ru" current={lang} onPick={setLang} />
        </div>
      </section>

      {/* Onboarding */}
      <section className="card" style={{ padding: 22, marginBottom: 18 }}>
        <h2 style={sectionTitle}>{t.settingsOnboarding}</h2>
        <p style={{ fontSize: 12, color: "var(--text-3)", margin: "0 0 14px", lineHeight: 1.5 }}>
          {t.settingsOnboardingHint}
        </p>
        <button onClick={replayOnboarding} className="btn btn-secondary">
          {t.settingsReplayOnboarding}
        </button>
      </section>

      {/* Password */}
      <section className="card" style={{ padding: 22, marginBottom: 18 }}>
        <h2 style={sectionTitle}>{t.settingsPassword}</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Field label={t.settingsOldPassword}>
            <input type="password" value={pwOld} onChange={(e) => setPwOld(e.target.value)} className="input" />
          </Field>
          <Field label={t.settingsNewPassword}>
            <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} className="input" />
          </Field>
          <Field label={t.settingsNewPassword}>
            <input type="password" value={pwNew2} onChange={(e) => setPwNew2(e.target.value)} className="input" />
          </Field>
        </div>
        <button onClick={changePassword} disabled={pwBusy} className="btn btn-secondary">
          {pwBusy ? t.loading : t.settingsChangePassword}
        </button>
      </section>

      {/* Editors */}
      <section className="card" style={{ padding: 22 }}>
        <h2 style={sectionTitle}>{t.settingsEditors}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {editors.map((ed) => (
            <div
              key={ed.id}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 12px",
                background: "var(--bg-2)",
                border: "1px solid var(--border-1)",
                borderRadius: "var(--radius-md)",
              }}
            >
              <div
                style={{
                  width: 32, height: 32, borderRadius: 100,
                  background: "var(--bg-3)", overflow: "hidden",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "var(--text-3)", flexShrink: 0,
                }}
              >
                {ed.avatar ? (
                  <img src={fileUrl("editors", ed.id, ed.avatar, "64x64")} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: 14 }}>{ed.name.slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: "var(--text-0)" }}>{ed.name}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>{ed.email}</div>
              </div>
              <span className={`badge ${ed.active ? "badge-published" : "badge-archived"}`}>
                {ed.active ? "active" : "inactive"}
              </span>
            </div>
          ))}
        </div>

        <h3 style={{ ...sectionTitle, fontSize: 14, margin: "0 0 10px" }}>{t.settingsInviteEditor}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Field label={t.settingsName} style={{ flex: 1, minWidth: 160 }}>
            <input value={inviteName} onChange={(e) => setInviteName(e.target.value)} className="input" placeholder="Anna Volkova" />
          </Field>
          <Field label={t.settingsInviteEmail} style={{ flex: 1, minWidth: 200 }}>
            <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="input" placeholder="anna@reverium.app" />
          </Field>
          <button onClick={invite} disabled={inviting} className="btn btn-primary">
            {inviting ? t.loading : t.settingsInviteCreate}
          </button>
        </div>
      </section>
    </div>
  );
}

// ── Language pill ─────────────────────────────────────────────────────────

function LangPill({
  label,
  code,
  current,
  onPick,
}: {
  label: string;
  code: Language;
  current: Language;
  onPick: (l: Language) => void;
}) {
  const active = current === code;
  return (
    <button
      onClick={() => onPick(code)}
      style={{
        padding: "8px 18px",
        background: active ? "rgba(184,134,74,0.15)" : "transparent",
        color: active ? "var(--accent, #b8864a)" : "var(--text-1)",
        border: "1px solid",
        borderColor: active ? "var(--accent, #b8864a)" : "var(--border-1)",
        borderRadius: 100,
        fontSize: 13,
        fontWeight: active ? 500 : 400,
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

function Field({
  label, children, style,
}: {
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <label className="field" style={style}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

function cryptoRandomPassword(): string {
  const a = new Uint8Array(12);
  crypto.getRandomValues(a);
  let s = "";
  for (let i = 0; i < a.length; i++) s += a[i].toString(36).padStart(2, "0");
  return s.slice(0, 16);
}

const sectionTitle: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: 18,
  fontWeight: 600,
  margin: "0 0 16px",
  color: "var(--text-0)",
  letterSpacing: "-0.01em",
};
